import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import api from '../services/api'
import { useToast } from '../context/ToastContext'
import { llamarTelefono } from '../utils/llamar'
import { Ico, Boton } from './ui'
import { confirmar } from '../context/DialogosContext'
import { useIsMobile } from '../hooks/useIsMobile'
import { abrirRutaInterna } from '../utils/rutaInterna'   // t315 · en la app del móvil, la conversación se abre dentro
import { t, tx } from '../utils/i18n'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

// Banner global: avisa en TODA la web cuando hay un hueco antes disponible para un
// paciente de la lista de espera, con acciones rápidas para reagendar (llamar por
// Zoiper, WhatsApp directo, adelantar, descartar).

// t85 (5-oct-2026) · EN EL MÓVIL, PLEGADO EN UNA LÍNEA. Las tarjetas de «Hueco antes para…» (con sus cuatro botones)
// ocupaban la primera pantalla entera en TODAS las pantallas del móvil. Por debajo de este ancho sale una sola línea,
// «N huecos antes para pacientes en espera · Ver», que se despliega al tocarla. En el ordenador, como siempre.
// Lo mide backend/routes/huecos-lista-plegados-en-el-movil.test.js a 390 px.
export const ANCHO_PLEGADO = 768
export const textoPlegado = (n) => (t('components.BannerHuecosLista.plural_huecos_antes_para_pacientes', { one: '{{count}} hueco antes para un paciente en espera', other: '{{count}} huecos antes para pacientes en espera' }, { count: n, _forma: (n === 1) ? 'one' : 'other' }))

// B-2238 (26-oct-2026) · EN EL ORDENADOR TAMBIÉN SE CIERRA. «Ocultar» deja las tarjetas en la misma línea que el móvil
// hasta que se vuelva a entrar (lo recuerda la pestaña, no la cuenta: mañana vuelven a salir desplegadas). Sin poder
// guardarlo (navegador privado), se pliega igual y vuelve a salir al recargar.
const CLAVE_OCULTO = 'huecos-lista-oculto'
function leerOculto() {
  try { return window.sessionStorage.getItem(CLAVE_OCULTO) === '1' } catch (_) { return false }
}
function guardarOculto(si) {
  try { if (si) window.sessionStorage.setItem(CLAVE_OCULTO, '1'); else window.sessionStorage.removeItem(CLAVE_OCULTO) } catch (_) { /* sin guardar */ }
}

export default function BannerHuecosLista() {
  const qc = useQueryClient()
  const toast = useToast()
  const [ocultas, setOcultas] = useState([])
  const plegable = useIsMobile(ANCHO_PLEGADO)
  const [desplegado, setDesplegado] = useState(false)
  const [ocultoPc, setOcultoPc] = useState(leerOculto)
  const cambiarOcultoPc = (si) => { setOcultoPc(si); guardarOculto(si) }

  // t85 · a quien no le toca la lista de espera (403) se le deja de preguntar cada minuto toda la sesión.
  const [sinPermiso, setSinPermiso] = useState(false)
  const { data: sugerencias = [] } = useQuery({
    queryKey: ['le-sugerencias-banner'],
    queryFn: () => api.get('/lista-espera/sugerencias').then(r => r.data).catch((e) => { if (e?.response?.status === 403) setSinPermiso(true); return [] }),
    refetchInterval: sinPermiso ? false : 60000,
    enabled: !sinPermiso,
    placeholderData: (prev) => prev,
  })
  const visibles = sugerencias.filter(s => !ocultas.includes(s.id))
  if (visibles.length === 0) return null
  const mostradas = visibles.slice(0, 4)

  // DOS PREGUNTAS, DOS COLUMNAS. `routes/listaEspera.js` ya devuelve las tres cosas: `tel` (el
  // teléfono para LLAMAR, donde un fijo es el número correcto), `movil` (al que se puede ESCRIBIR,
  // que nunca es un fijo) y `motivo_sin_movil` (por qué no lo hay). El servidor se partió en dos
  // preguntas y esta pantalla se había quedado con una: el botón de WhatsApp colgaba de `s.tel`,
  // así que a quien tiene un fijo le abría el chat contra el fijo de casa —un mensaje que no lee
  // nadie y que Meta cobra igual— y a quien tiene el móvil en «Teléfono 2» no se le podía escribir.
  const abrirWhatsApp = async (s) => {
    if (!s.movil) { toast.error(s.motivo_sin_movil || t('components.BannerHuecosLista.no_hay_ningun_movil_al', 'No hay ningún móvil al que escribir')); return }
    try {
      const tel = String(s.movil).replace(/\D/g, '')
      const telF = (tel.startsWith('6') || tel.startsWith('7') || tel.startsWith('9')) ? '34' + tel : tel
      const conv = await api.post('/conversaciones', { telefono: telF, nombre_contacto: s.paciente_nombre || s.texto || 'Paciente', paciente_id: s.paciente_app_id || null })
      abrirRutaInterna(`/comunicacion?conv=${conv.data.id}`)
    } catch (e) { toast.error(e.response?.data?.error || e.message) }
  }
  const adelantar = async (s) => {
    const h = s.sugerencia
    if (!h) return
    const ok = await confirmar({
      titulo: t('components.BannerHuecosLista.adelantar_la_cita', 'Adelantar la cita'),
      mensaje: t('components.BannerHuecosLista.adelantar_la_cita_de_al', '¿Adelantar la cita de {{x}} al {{join}} a las {{hora}} con {{doctor}}?', { x: s.paciente_nombre || s.texto, join: String(h.fecha).split('-').reverse().join('/'), hora: h.hora, doctor: h.doctor }),
      detalle: t('components.BannerHuecosLista.se_anula_la_cita_original', 'Se anula la cita original y se crea la nueva.'),
      textoConfirmar: t('components.BannerHuecosLista.adelantar', 'Adelantar'), tipo: 'danger',
    })
    if (!ok) return
    try {
      await api.post(`/lista-espera/${s.id}/adelantar`, { fecha: h.fecha, hora: h.hora, id_usu: h.id_usu, id_opc: h.id_opc, duracion_min: h.duracion_min })
      toast.success(t('components.BannerHuecosLista.cita_adelantada_la_original_queda', 'Cita adelantada. La original queda anulada.'))
      qc.invalidateQueries({ queryKey: ['le-sugerencias-banner'] }); qc.invalidateQueries({ queryKey: ['lista-espera'] })
    } catch (e) { toast.error(e.response?.data?.error || e.message) }
  }
  const descartar = async (s) => {
    try { await api.post(`/lista-espera/${s.id}/descartar-sugerencia`) } catch (_) {}
    setOcultas(o => [...o, s.id])
    qc.invalidateQueries({ queryKey: ['le-sugerencias-banner'] })
  }

  // B-711 · POR QUE LLEVA z-index. Este banner iba en flujo y SIN z-index, y justo encima de el,
  // dentro del MISMO contenedor de scroll, habia dos barras `position: sticky; top: 0` que si lo
  // tenian: `DentroDeClinica` (60) y `AccesoSoporteBanner` (30). Al bajar la pagina se quedaban
  // clavadas y pasaban POR ENCIMA del aviso. (Desde el 3-oct las dos van fuera del <main>, en flujo y
  // sin z-index: ya no pueden taparlo.)
  // t302 (19-oct-2026) · Y SU CAPA ES LA DE LOS AVISOS DE LA PAGINA, de la escala de index.css («LAS
  // CAPAS DE LA APLICACIÓN»). Con el 70 de B-711 quedaba por encima de la barra de arriba (30) y de
  // todo lo que se despliega de ella: se abria el menu del avatar, se pulsaba «Mi perfil» y el clic
  // caia en este aviso. Tambien tapaba el menu lateral del movil (50). Ahora: encima de la pagina,
  // debajo de la barra, de sus desplegables y del menu lateral.
  // B-2238 (26-oct-2026) · Y NO TAPA NADA: «tapa otro tipo de mensajes y títulos de pantalla». Las pantallas que llegan
  // hasta el borde del <main> (la ficha, Comunicación, el chat del equipo, Formación) se suben 24 px para comerse su
  // relleno, y con este aviso encima se metían 8 px por debajo de él: en el móvil, el «Ir a Agenda» de la ficha quedaba
  // medio tapado. Ahora va en la PILA DE AVISOS de la página (Layout, `data-avisos-pagina`), que deja debajo esos 24 px:
  // el aviso ocupa su sitio, se apila con las comprobaciones del día y la pantalla empieza justo debajo. Su margen lo pone
  // la pila, no él. Lo mide backend/routes/aviso-huecos-no-tapa.test.js.
  const plegado = plegable ? !desplegado : ocultoPc
  const conLinea = plegable || ocultoPc
  const alternar = () => (plegable ? setDesplegado((v) => !v) : cambiarOcultoPc(!ocultoPc))
  return (
    <div data-banner-huecos style={{ position: 'relative', zIndex: 'var(--capa-aviso-pagina)', display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {conLinea && (
        <button type="button" data-huecos-plegado="" aria-expanded={plegado ? 'false' : 'true'} onClick={alternar}
          style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', minHeight: '44px', padding: '8px 12px', textAlign: 'left', cursor: 'pointer',
            background: 'color-mix(in srgb, var(--success) 9%, var(--surface))', border: '1px solid color-mix(in srgb, var(--success) 32%, var(--border))', borderRadius: 'var(--radius-lg)', color: 'var(--text-primary)' }}>
          <span style={{ color: 'var(--success)', display: 'inline-flex', flexShrink: 0 }}><Ico n="varita" s={16} /></span>
          <span style={{ flex: 1, minWidth: 0, fontSize: '13px', fontWeight: 700, overflowWrap: 'anywhere' }}>{textoPlegado(visibles.length)}</span>
          <span style={{ flexShrink: 0, fontSize: '13px', fontWeight: 700, color: 'var(--primary)' }}>{plegado ? t('components.BannerHuecosLista.ver', 'Ver') : t('components.BannerHuecosLista.ocultar', 'Ocultar')}</span>
        </button>
      )}
      {!plegado && mostradas.map(s => {
        const h = s.sugerencia || {}
        return (
          <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', padding: '11px 14px', background: 'color-mix(in srgb, var(--success) 9%, var(--surface))', border: '1px solid color-mix(in srgb, var(--success) 32%, var(--border))', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)' }}>
            <span style={{ width: '34px', height: '34px', borderRadius: '10px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--success-bg)', fontSize: '18px', color: 'var(--success)' }}><Ico n="varita" s={18} /></span>
            <div style={{ flex: 1, minWidth: '220px' }}>
              <div style={{ fontSize: '13px', fontWeight: 800, color: 'var(--text-primary)' }}>
                {tx('components.BannerHuecosLista.hueco_antes_para', 'Hueco antes para {{x}}', { x: s.paciente_nombre || s.texto || t('components.BannerHuecosLista.paciente', 'paciente') })}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'inline-flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                <span style={{ color: 'var(--success)', display: 'inline-flex' }}><Ico n="calendarCheck" s={14} c="var(--success)" /></span>
                {tx('components.BannerHuecosLista.con', '{{join}} · {{hora}} con {{doctor}}{{x}}', { join: String(h.fecha).split('-').reverse().join('/'), hora: h.hora, doctor: h.doctor, x: s.cita_actual_fmt ? t('components.BannerHuecosLista.cita_actual', ' · cita actual: {{cita_actual_fmt}}', { cita_actual_fmt: s.cita_actual_fmt }) : '' })}
              </div>
            </div>
            {s.tel && <Boton variante="neutro" icon="phone" onClick={() => llamarTelefono(s.tel, toast, { pacienteId: s.paciente_app_id || null, nombre: s.paciente_nombre || s.texto || '' })}>{t('components.BannerHuecosLista.llamar', 'Llamar')}</Boton>}
            {s.movil
              ? <Boton variante="neutro" icon="whatsapp" onClick={() => abrirWhatsApp(s)}>{t('components.BannerHuecosLista.whatsapp', 'WhatsApp')}</Boton>
              : <span title={s.motivo_sin_movil || ''} style={{ fontSize: '11.5px', color: 'var(--warning)' }}>
                  {s.motivo_sin_movil || t('components.BannerHuecosLista.sin_movil_al_que_escribir', 'Sin móvil al que escribir')}
                </span>}
            <Boton variante="primario" icon="check" onClick={() => adelantar(s)}>{t('components.BannerHuecosLista.adelantar', 'Adelantar')}</Boton>
            <Boton variante="neutro" onClick={() => descartar(s)}>{t('components.BannerHuecosLista.descartar', 'Descartar')}</Boton>
          </div>
        )
      })}
      {!plegado && (visibles.length > mostradas.length || !plegable) && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap', fontSize: '12px', color: 'var(--text-muted)' }}>
          <span>{visibles.length > mostradas.length ? t('components.BannerHuecosLista.mas_en_lista_de_espera', '+{{x}} más en Lista de espera', { x: visibles.length - mostradas.length }) : ''}</span>
          {/* B-2238 · en el ordenador, cerrarlo: queda en una línea («… · Ver») hasta que se vuelva a entrar. */}
          {!plegable && (
            <button type="button" data-huecos-ocultar="" onClick={() => cambiarOcultoPc(true)}
              style={{ marginLeft: 'auto', background: 'none', border: 'none', padding: '4px 2px', cursor: 'pointer', fontSize: '12.5px', fontWeight: 700, color: 'var(--primary)' }}>
              {t('components.BannerHuecosLista.ocultar', 'Ocultar')}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
